import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { Container } from "@mui/system";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import MySnackbar from "../MySnackbar";
import { Context } from "../..";
import useSnackbar from "../../hooks/useSnackbar";
import useUnsavedChangesWarning from "../../hooks/useUnsavedChangesWarning";
import { createCollageService, deleteCollage, fetchOneCollage, getServicesForCollage,  } from "../../http/clinicServiceAPI";
import {getImageUrl} from "../../helpers/getImageUrl";

const API_URL = process.env.REACT_APP_API_URL || window.API_URL;

const CreateUpdateCollage = () => {
  const [collageServiceName, setCollageServiceName] = useState('');
  const [collageDescription, setCollageDescription] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [existingImagesRemove, setExistingImagesRemove] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceError, setServiceError] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const navigate = useNavigate();
  const snack = useSnackbar(false, "Ошибка");
  const params = useParams();
  const { user } = useContext(Context);
  const { confirmLeave } = useUnsavedChangesWarning(hasUnsaved);
  const isNew = params.id === "new";
  const pageTitle = isNew ? "Создание до/после" : "Редактирование до/после";

  useEffect(() => {
    fetchServicesList();
    if (params.id !== "new") fetchCollageData(params.id);
  }, [params.id]);

  const fetchCollageData = async (id) => {
    try {
      const data = await fetchOneCollage(id);
      setCollageServiceName(data['ClinicService.name']);
      setCollageDescription(data.description || '');
      setSelectedService(data.serviceId);
      setExistingImages(data.images.map((image) => image.image));
      setHasUnsaved(false);
    } catch (error) {
      snack.handleChange(true, "Ошибка при загрузке данных услуги.");
    }
  };

  const fetchServicesList = async () => {
    const data = await getServicesForCollage(user.user.clinicId);
    setServices(data);
  };

  const handleServiceChange = (event) => {
    setSelectedService(event.target.value);
    setServiceError(false);
    setHasUnsaved(true);
  };

  const handleImagesChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length + images.length > 10) {
      snack.handleChange(true, "Можно загрузить не более 10 изображений");
      return;
    }
    setImages([...images, ...files]);
    setHasUnsaved(true);
  };

  const handleDeleteImage = (index, isExistingImage = false) => {
    if (isExistingImage) {
      setExistingImagesRemove([...existingImagesRemove, existingImages[index]]);
      setExistingImages(existingImages.filter((_, i) => i !== index));
      setHasUnsaved(true);
    } else 
      setImages(images.filter((_, i) => i !== index));
      setHasUnsaved(true);
  };

  const save = async () => {
    if (!selectedService && params.id === "new") {
      setServiceError(true);
      return;
    }

    const formData = new FormData();
    formData.append("description", collageDescription || "");
    formData.append("authorId", user.user.id);
    formData.append("serviceId", selectedService);
    formData.append("clinicId", user.user.clinicId);

    if (params.id !== "new") {
      formData.append("id", params.id);
      formData.append("imageRemove", []);
      existingImagesRemove.forEach(imageName => {
        formData.append('imageRemove', imageName);
      });
    }

    images.forEach((image, index) => {
      formData.append(`image_${index}`, image);
    });

    try {
      await createCollageService(formData);
      snack.handleChange(false, "Коллаж успешно сохранен.");
      setHasUnsaved(false);
      navigate("/examples");
    } catch (error) {
      snack.handleChange(true, "Ошибка при сохранении до/после.");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Вы уверены, что хотите удалить этот до/после?")) {
      try {
        await deleteCollage(params.id);
        snack.handleChange(false, "Коллаж удален.");
        navigate("/examples");
      } catch (error) {
        snack.handleChange(true, "Ошибка при удалении до/после.");
      }
    }
  };

  const handleBack = () => {
    if (!confirmLeave()) {
      return;
    }
    navigate(-1);
  };

  return (
    <Container sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
      <MySnackbar {...snack} />

      <Stack spacing={1} sx={{ mb: 3, width: "100%" }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: "flex-start" }}
          onClick={handleBack}
        >
          Назад
        </Button>
        <Typography variant="h4" fontWeight={600}>
          {pageTitle}
        </Typography>
        {!isNew && (
          <Typography variant="body2" component={"span"} color="templateText.secondary">
            Статус:
            <Chip
              size="small"
              color="primary"
              label="Сохранено"
              sx={{ ml: 0.5 }}
            />
          </Typography>
        )}
      </Stack>

      <Box sx={{ maxWidth: 500, margin: "0 auto", padding: 3 }}>
        <form>
          {!isNew ? (
            <TextField
              label="Выбрать услугу"
              variant="outlined"
              fullWidth
              value={collageServiceName || ""}
              disabled
              margin="normal"
            />
          ) : (
            <>
              {services.length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                  Для создания до/после нет доступных услуг
                </Typography>
              ) : (
                <FormControl sx={{ mb: 1, width: "100%" }} error={serviceError}>
                  <InputLabel id="serviceLabel">Выбрать услугу</InputLabel>
                  <Select
                    labelId="serviceLabel"
                    value={selectedService}
                    onChange={handleServiceChange}
                    label="Выбрать услугу"
                  >
                    {services.map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {serviceError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      Пожалуйста, выберите услугу.
                    </Typography>
                  )}
                </FormControl>
              )}
            </>
          )}

          <TextField
            label="Описание"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={collageDescription}
            onChange={(e) => {
              setCollageDescription(e.target.value);
              setHasUnsaved(true);
            }}
            margin="normal"
          />

          <Box sx={{ marginBottom: 2 }}>
            <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} fullWidth>
              Загрузить изображения (макс. 10)
              <input type="file" hidden accept="image/*" multiple onChange={handleImagesChange} />
            </Button>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              {existingImages.map((image, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ position: "relative" }}>
                    <img
                      src={getImageUrl(image)}                      alt={`Existing Preview ${index}`}
                      style={{ width: "100%", height: "200px", objectFit: "cover", borderRadius: 4 }}
                    />
                    <CancelIcon
                      sx={{ position: "absolute", top: 8, right: 8, cursor: "pointer", color: "red" }}
                      onClick={() => handleDeleteImage(index, true)}
                    />
                  </Box>
                </Grid>
              ))}

              {images.map((image, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ position: "relative" }}>
                    <img
                      src={URL.createObjectURL(image)}
                      alt={`Preview ${index}`}
                      style={{ width: "100%", height: "200px", objectFit: "cover", borderRadius: 4 }}
                    />
                    <CancelIcon
                      sx={{ position: "absolute", top: 8, right: 8, cursor: "pointer", color: "red" }}
                      onClick={() => handleDeleteImage(index)}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                onClick={save}
                disabled={params.id === "new" && images.length === 0}
                fullWidth
              >
                Сохранить
              </Button>
            </Grid>
            {params.id !== "new" && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={params.id === "new" && (images.length === 0 && existingImages.length === 0)}
                  onClick={handleDelete}
                  fullWidth
                  startIcon={<DeleteIcon />}
                >
                  Удалить
                </Button>
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
    </Container>
  );
};


export default CreateUpdateCollage;
